import React, { useMemo, useState, useCallback } from 'react';
import { Button, Typography, Space, Input, message, Tag } from 'antd';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import EnhancedTour from '../../../Common/EnhancedTour';
import { useEffect } from 'react';

const Step2BaseOutline = ({ keyword, onSelectOutline }) => {
  // ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('generation_step2');

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  const outlines = useMemo(
    () => [
      {
        title: '【徹底比較】SEO と AEO の違い・最新トレンドと実践ポイント',
        sections: [
          'H2: SEO と AEO の基本（定義と目的の違い）',
          'H3: そもそもの目的の違い',
          'H3: 期待する成果とKPI',
          'H2: Google の進化で何が変わったか',
          'H3: 検索→対話理解への変化',
          'H3: 生成の影響と注意点',
          'H2: 実務での影響',
          'H3: コンテンツ企画',
          'H3: 構成と表現',
          'H3: 計測と評価',
          'H2: 成功事例',
          'H3: BtoB',
          'H3: BtoC',
          'H3: メディア',
          'H2: 今日からできる実践チェックリスト',
        ],
      },
      {
        title: 'もう古い？ いま効くSEO/AEOの考え方と実装手順',
        sections: [
          'H2: よくある誤解',
          'H3: キーワード密度は重要か',
          'H3: 長文なら上がる？',
          'H2: いま有効なアプローチ',
          'H3: 体験価値の組み込み',
          'H3: 対話理解への最適化',
          'H2: 実装のロードマップ',
          'H3: 調査',
          'H3: 設計',
          'H3: 検証',
          'H2: 失敗を避けるポイント',
          'H2: まとめ（次のアクション）',
        ],
      },
      {
        title: 'はじめてのAEO完全ガイド（ビジネスインパクトから理解する）',
        sections: [
          'H2: AEOの基礎',
          'H3: 目的',
          'H3: 指標',
          'H3: SERPの変化',
          'H2: SEOとの位置づけと連携',
          'H2: ユースケース',
          'H3: SaaS',
          'H3: EC',
          'H3: メディア',
          'H2: 導入の進め方',
          'H3: 体制',
          'H3: KPI',
          'H3: ツール',
          'H2: まとめ（定着と継続改善）',
        ],
      },
    ],
    [],
  );

  const [freeOutline, setFreeOutline] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);

  const parseSection = useCallback(text => {
    const match = /^(H[123])\s*:\s*(.*)$/.exec(text.trim());
    if (!match) return { level: 'H2', title: text };
    return { level: match[1], title: match[2] };
  }, []);

  // Tourステップ定義
  const tourSteps = useMemo(() => {
    if (tourLoading || !TOUR_CONFIGS.generation_step2) return [];
    const steps = TOUR_CONFIGS.generation_step2.steps.map(step => ({
      ...step,
      target: step.target || null,
    }));
    return steps;
  }, [tourLoading]);

  return (
    <div className="v2-form-narrow">
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={currentStep}
          onChange={updateCurrentStep}
          tourTitle="STEP2: ベースアウトライン"
        />
      )}

      <div className="v2-intro-box" style={{ marginBottom: 24 }}>
        <Typography.Paragraph className="v2-intro-text" style={{ margin: 0 }}>
          最もイメージに近いベースのアウトラインを選択してください。選択されたアウトラインを元に、見出し構成を生成します。
          <br />
          さらに、生成時の追加指示を入力することもできます。
        </Typography.Paragraph>
      </div>

      <div className="outline-grid">
        {outlines.map((o, idx) => (
          <div
            key={idx}
            className={`outline-card-wrap`}
            role="button"
            tabIndex={0}
            onClick={() => setSelectedIndex(idx)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') setSelectedIndex(idx);
            }}
          >
            {selectedIndex === idx && (
              <div className="outline-selected-badge">これをベースに生成</div>
            )}
            <div
              className={`outline-card ${selectedIndex === idx ? 'selected' : ''}`}
            >
              <h4>{o.title}</h4>
              <div>
                {o.sections.map((s, i) => {
                  const { level, title } = parseSection(s);
                  const indent = level === 'H2' ? 0 : 16;
                  return (
                    <div
                      key={i}
                      className="outline-line"
                      style={{ paddingLeft: indent }}
                    >
                      <Tag
                        color={
                          level === 'H4'
                            ? 'green'
                            : level === 'H3'
                              ? 'yellow'
                              : 'orange'
                        }
                        style={{ marginRight: 4, borderRadius: 999 }}
                      >
                        {level}
                      </Tag>
                      <span className="outline-text">{title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="outline-actions" style={{ marginTop: 24 }}>
        <Button
          type="primary"
          size="large"
          block
          disabled={selectedIndex === null}
          onClick={() => {
            if (selectedIndex === null) return;
            onSelectOutline({
              index: selectedIndex,
              outline: outlines[selectedIndex],
              freeText: freeOutline.trim() || '',
            });
          }}
        >
          構成案を生成する
        </Button>
      </div>
    </div>
  );
};

export default Step2BaseOutline;
